import React, { useMemo, useState } from 'react'
import type { GetProp, TableProps } from 'antd'
import { Table, Flex } from 'antd'
import { SeedTracker, SeedPedigree } from 'modules/domain/seedTracker/types'
import SeedTrackerSelectors from 'modules/domain/seedTracker/selectors'
import SeedTrackerActions from 'modules/domain/seedTracker/duck'
import { useSeedTrackerList } from 'modules/domain/seedTracker/hooks'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useAction, useDidMount, useHistoryPush } from '@agro-club/frontend-shared'
import { Progress } from 'modules/types'
import styled from 'styled-components'
import Routes from '../routes'
import { formatMt, formatMtAsNegative } from '../formatMt'
import { summaryFooterWrapStyle, summaryLabelStyle, summaryValueStyle } from '../styles'
import '../styles.css'

type ColumnsType<T extends object = object> = TableProps<T>['columns']
type TablePaginationConfig = Exclude<GetProp<TableProps, 'pagination'>, boolean>

const emptyMetric = '—'

const totalColumnWidths = {
  total_available_mt: 220,
  total_pending_sale_mt: 220,
  total_on_hand_mt: 220,
} as const

function formatValue(v: string | null | undefined): string {
  if (v == null || v === '') {
    return emptyMetric
  }
  return v
}

// Nested table data type
type NestedTableData = {
  key: string
  pedigree: SeedPedigree
  available_mt: string | null | undefined
  pending_sale_mt: string | null | undefined
  on_hand_mt: string | null | undefined
}

// Styled component for Pedigree label
const PedigreeLabel = styled.div<{ backgroundColor: string }>`
  display: flex;
  height: 22px;
  padding: 0 12px;
  align-items: center;
  gap: 8px;
  border-radius: 12px;
  background: ${(props) => props.backgroundColor};
  color: white;
  font-size: 12px;
  width: fit-content;
`

// Function to get background color for each pedigree type
const getPedigreeBackgroundColor = (pedigree: SeedPedigree): string => {
  switch (pedigree) {
    case SeedPedigree.Certified:
      return '#20B02E'
    case SeedPedigree.Registered:
      return '#5C3700'
    case SeedPedigree.Foundation:
      return '#FFAB2E'
    case SeedPedigree.Select:
      return '#037BFF'
    case SeedPedigree.Breeder:
      return '#776D6D'
    default:
      return '#776D6D'
  }
}

function getColumnKey(column: NonNullable<TableProps<SeedTracker>['columns']>[number], index: number): string {
  if (column.key != null) {
    return String(column.key)
  }
  if ('dataIndex' in column && column.dataIndex != null) {
    return Array.isArray(column.dataIndex) ? column.dataIndex.join('.') : String(column.dataIndex)
  }
  return String(index)
}

const SeedTrackerList: React.FC = () => {
  const { t } = useTranslation(['seedTracker', 'labels'])
  const push = useHistoryPush()
  const [progress, data = [], meta] = useSeedTrackerList({})
  const sortingUpdated = useAction(SeedTrackerActions.sortingUpdated)
  const listRequested = useAction(SeedTrackerActions.listRequested)
  const filterValue = useSelector(SeedTrackerSelectors.filter)
  const total = useSelector(SeedTrackerSelectors.total)
  const page = useSelector(SeedTrackerSelectors.page)
  const pageSize = useSelector(SeedTrackerSelectors.pageSize)
  const { sort_field, sort_reversed } = useSelector(SeedTrackerSelectors.sorting)
  const [tableParams, setTableParams] = useState<{ pagination?: TablePaginationConfig; filters?: Record<string, any> }>(
    {
      pagination: {
        showSizeChanger: false,
        position: ['bottomLeft'],
      },
    },
  )
  const [footerTotals, setFooterTotals] = useState<{
    total_available_mt?: string | null
    total_pending_sale_mt?: string | null
    total_on_hand_mt?: string | null
  }>({})

  // Update footer totals when metadata changes
  React.useEffect(() => {
    if (meta?.totals) {
      setFooterTotals(meta.totals)
    }
  }, [meta])

  // Nested table columns
  const nestedColumns: ColumnsType<NestedTableData> = [
    {
      title: 'Pedigree',
      dataIndex: 'pedigree',
      key: 'pedigree',
      render: (pedigree: SeedPedigree) => (
        <PedigreeLabel backgroundColor={getPedigreeBackgroundColor(pedigree)}>{pedigree}</PedigreeLabel>
      ),
    },
    {
      title: 'Available (MT)',
      dataIndex: 'available_mt',
      key: 'available_mt',
      width: totalColumnWidths.total_available_mt,
      align: 'right',
      render: (value: string | null | undefined) => formatMt(value),
    },
    {
      title: 'Pending Sale (MT)',
      dataIndex: 'pending_sale_mt',
      key: 'pending_sale_mt',
      width: totalColumnWidths.total_pending_sale_mt,
      align: 'right',
      render: (value: string | null | undefined) => formatMtAsNegative(value),
    },
    {
      title: 'On Hand (MT)',
      dataIndex: 'on_hand_mt',
      key: 'on_hand_mt',
      width: totalColumnWidths.total_on_hand_mt,
      align: 'right',
      render: (value: string | null | undefined) => formatValue(value),
    },
  ]

  // Main table columns
  const columns: ColumnsType<SeedTracker> = [
    {
      title: t('list.tableHeaders.brand'),
      dataIndex: 'brand',
      key: 'brand',
    },
    {
      title: t('list.tableHeaders.variety'),
      dataIndex: 'variety',
      key: 'variety',
      sorter: true,
      sortOrder: sort_field === 'variety' ? (sort_reversed ? 'descend' : 'ascend') : null,
    },
    {
      title: t('list.tableHeaders.crop'),
      dataIndex: 'seed_crop_name',
      key: 'seed_crop_name',
      sorter: true,
      sortOrder: sort_field === 'seed_crop_name' ? (sort_reversed ? 'descend' : 'ascend') : null,
    },
    {
      title: t('list.tableHeaders.class'),
      dataIndex: 'seed_class_name',
      key: 'seed_class_name',
      sorter: true,
      sortOrder: sort_field === 'seed_class_name' ? (sort_reversed ? 'descend' : 'ascend') : null,
    },
    {
      title: t('list.tableHeaders.totalAvailableMT'),
      dataIndex: 'total_available_mt',
      key: 'total_available_mt',
      width: totalColumnWidths.total_available_mt,
      align: 'right',
      render: (value: string | null | undefined) => formatMt(value),
    },
    {
      title: t('list.tableHeaders.totalPendingSaleMT'),
      dataIndex: 'total_pending_sale_mt',
      key: 'total_pending_sale_mt',
      width: totalColumnWidths.total_pending_sale_mt,
      align: 'right',
      render: (value: string | null | undefined) => formatMtAsNegative(value),
    },
    {
      title: t('list.tableHeaders.totalOnHandMT'),
      dataIndex: 'total_on_hand_mt',
      key: 'total_on_hand_mt',
      width: totalColumnWidths.total_on_hand_mt,
      align: 'right',
      render: (value: string | null | undefined) => formatValue(value),
    },
  ]

  React.useEffect(() => {
    setTableParams((old) => ({
      ...old,
      pagination: {
        ...old.pagination,
        current: page,
        pageSize,
        total,
      },
    }))
  }, [page, pageSize, total])

  useDidMount(() => {
    listRequested({})
  })

  const handleTableChange: TableProps<SeedTracker>['onChange'] = (pagination, filters, sorter) => {
    setTableParams({
      pagination,
      filters,
    })

    // Handle sorting
    if (sorter && !Array.isArray(sorter) && sorter.field) {
      const sortField = sorter.field as keyof SeedTracker
      const sortReversed = sorter.order === 'descend'
      sortingUpdated({ sort_field: sortField, sort_reversed: sortReversed })
    } else {
      // Clear sorting when no sorter is active
      sortingUpdated({ sort_field: undefined, sort_reversed: false })
    }

    if (pagination.current) {
      listRequested({ page: pagination.current })
    }
  }

  const rowKey: TableProps<SeedTracker>['rowKey'] = (record) => record.id

  const expandable: TableProps<SeedTracker>['expandable'] = {
    expandedRowRender: (record) => {
      const nestedData: NestedTableData[] = (record.pedigree_breakdown || []).map((breakdown, index) => ({
        key: String(index),
        pedigree: breakdown.pedigree,
        available_mt: breakdown.available_mt,
        pending_sale_mt: breakdown.pending_sale_mt,
        on_hand_mt: breakdown.on_hand_mt,
      }))

      return (
        <div style={{ padding: '8px 8px 8px 24px' }}>
          <Table<NestedTableData>
            columns={nestedColumns}
            dataSource={nestedData}
            pagination={false}
            size="small"
            rowKey="key"
          />
        </div>
      )
    },
    rowExpandable: () => true,
  }

  const onRow: TableProps<SeedTracker>['onRow'] = (record) => {
    return {
      onClick: () => {
        push({ route: Routes.SeedInventoryList, params: { id: record.id } })
      },
    }
  }

  const isFilterApplied = Object.values(filterValue).some(Boolean)
  const showSummary = data.length > 0

  const footerColumns = useMemo<TableProps<SeedTracker>['columns']>(() => {
    return columns?.map((column, index) => {
      const key = getColumnKey(column, index)
      const totalColumnKeys = ['total_available_mt', 'total_pending_sale_mt', 'total_on_hand_mt']
      const isTotalColumn = totalColumnKeys.includes(key)
      let value = isTotalColumn ? footerTotals[key as keyof typeof footerTotals] || emptyMetric : ''
      if (key === 'total_available_mt') {
        value = formatMt(value)
      } else if (key === 'total_pending_sale_mt') {
        value = formatMtAsNegative(value)
      }

      return {
        ...column,
        render: () =>
          isTotalColumn ? (
            <Flex vertical>
              <span style={summaryLabelStyle}>Total</span>
              <span style={summaryValueStyle}>{value}</span>
            </Flex>
          ) : (
            ''
          ),
      }
    })
  }, [columns, footerTotals])

  return (
    <>
      {showSummary && (
        <div style={summaryFooterWrapStyle}>
          <Table<SeedTracker>
            columns={footerColumns}
            dataSource={[{} as SeedTracker]}
            rowKey={() => 'seed-tracker-summary-row'}
            showHeader={false}
            pagination={false}
          />
        </div>
      )}
      <Table<SeedTracker>
        columns={columns}
        dataSource={data}
        rowKey={rowKey}
        loading={progress === Progress.WORK}
        onChange={handleTableChange}
        pagination={tableParams.pagination}
        expandable={expandable}
        onRow={onRow}
        rowClassName={'row-cursor-pointer'}
        locale={{
          emptyText: isFilterApplied ? t('list.emptyFilterMsg') : t('list.emptyMsg'),
        }}
      />
    </>
  )
}

export default SeedTrackerList
